<template>
  <StackPageLayout @close="handleClose">
    <template #header>
      <div class="flex w-full items-center justify-between">
        <PageTitle> Edit Cashiers </PageTitle>

        <RouterLink
          #default="{ navigate }"
          custom
          :to="{ name: 'settingsCashierCreate' }"
        >
          <ButtonAction @click="navigate">
            Add Cashier
          </ButtonAction>
        </RouterLink>
      </div>
    </template>

    <div
      ref="scrollContainerRef"
      class="h-full overflow-y-auto rounded-lg bg-background py-3"
    >
      <div class="h-full p-3">
        <QueryLoading v-if="isLoading" />
        <QueryError v-else-if="isError">
          Failed to retrieve cashiers
        </QueryError>
        <template v-else>
          <div class="flex flex-col px-3">
            <SearchInput v-model="searchString" />
          </div>
          <template v-if="filteredCashiers.length">
            <div
              v-for="cashier in filteredCashiers"
              :key="cashier.id"
              class="flex"
            >
              <RouterLink
                #default="{ navigate }"
                :to="{
                  name: 'settingsCashierEdit',
                  params: { cashierId: cashier.id },
                }"
                custom
              >
                <div
                  class="flex w-full cursor-pointer flex-row items-center space-x-3 p-3"
                  @click="navigate"
                >
                  <ListItemCashier :cashier="cashier" />
                </div>
              </RouterLink>
              <button
                type="button"
                class="flex w-8 items-center justify-center"
                @click="handleClickDots(cashier.id)"
              >
                <IconVerticalEllipsis class="size-6" />
              </button>
            </div>
          </template>
          <div
            v-else
            class="mt-5 text-center font-medium text-background-fg/70"
          >
            No cashiers to display
          </div>
        </template>
      </div>
    </div>
  </StackPageLayout>

  <BaseDrawer
    shrink
    :is-open="isEditDeleteDrawerOpen"
    @close="cancelEditDelete"
  >
    <div class="h-full overflow-y-auto px-3 pb-4">
      <ActionItemSmall
        label="Edit Cashier"
        :icon="IconEdit"
        @click="confirmEditDelete('edit')"
      />

      <ActionItemSmall
        label="Delete Cashier"
        :icon="IconTrash"
        @click="confirmEditDelete('delete')"
      />
    </div>
  </BaseDrawer>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    description="Are you sure you wish to delete this cashier?"
    confirm-label="Delete Cashier"
    cancel-label="No"
  />
</template>

<script setup lang="ts">
import {
  IconEdit,
  IconTrash,
  IconVerticalEllipsis,
} from "@/icons"
import { deleteCashier } from "@/services/cashiers/deleteCashier"
import { useCashiers } from "@/services/cashiers/listCashiers"

const router = useRouter()
const handleClose = () => router.push({ name: "moreMenu" })

const { data: cashiers, isError, isLoading } = useCashiers()
const searchString = ref<string>("")

const filteredCashiers = computed(() => {
  if (!searchString.value) return cashiers.value
  const searchLower = searchString.value.toLowerCase()
  return (
    cashiers.value?.filter((cashier) =>
      cashier.username.toLowerCase().includes(searchLower)
    ) ?? []
  )
})

const {
  isRevealed: isEditDeleteDrawerOpen,
  reveal: showEditDeleteDraw,
  confirm: confirmEditDelete,
  cancel: cancelEditDelete,
} = useConfirmDialog()
const handleClickDots = async (cashierId: string) => {
  const { data, isCanceled } = await showEditDeleteDraw()
  if (isCanceled) return

  if (data === "edit") {
    router.push({
      name: "settingsCashierEdit",
      params: {
        cashierId: cashierId,
      },
    })
  } else if (data === "delete") {
    if (deleteConfirmDialogRef.value === null) return
    const { isCanceled: isDeleteCancelled } =
      await deleteConfirmDialogRef.value.reveal()
    if (isDeleteCancelled) return

    try {
      await deleteCashier(cashierId)
      toast.success("Cashier deleted!")
    } catch {
      toast.error("Failed to delete cashier")
    }
  }
}

const deleteConfirmDialogRef = useTemplateRef(
  "deleteConfirmDialogRef"
)

const scrollContainerRef = useTemplateRef(
  "scrollContainerRef"
)

onMounted(() => {
  // Initially hide search bar
  scrollContainerRef.value?.scrollTo({
    top: 64,
    behavior: "instant",
  })
})
</script>
